import { getPath, useNavigation } from '@speeki/client-admin';
import { Skeleton, Typography } from '@speeki/global-ui-components';
import { IconManager } from '@speeki/icons';

import TargetsBackground from '@assets/svgs/TargetsBackground.svg';

import { CardBadge } from '@components/atoms/CardBadge/CardBadge';
import { CardLink } from '@components/atoms/CardLink/CardLink';
import { PlaceholderCard } from '@components/atoms/PlaceholderCard/PlaceholderCard';
import { StatisticsCardEmptyState } from '@components/molecules/StatisticsCards/components/StatisticsCardEmptyState/StatisticsCardEmptyState';
import { ActionWrapper } from '@components/molecules/StatisticsCards/StatisticsCards.styled';

import {
    Target_Direction,
    TargetsCardDocument,
} from '@graphql/generated/graphql';

import { calculateTargetUnitsValue } from '@utils/helpers';

import { useQuery } from '@apollo/client';

import {
    ContentWrapper,
    Ornament,
    TargetItem,
    TargetItemContent,
    TargetItemContentValues,
    TargetItemPercentageValue,
    TargetItemPercentageWrapper,
    TargetItemValues,
    TargetsCardWrapper,
    TargetsHeader,
} from './TargetsCard.styled';

export interface TargetsCardProps {
    hidden?: boolean;
}

const renderPercentageValue = (
    percentageValue: number,
    direction: Target_Direction,
) => {
    if (percentageValue <= 0) return null;

    const icon =
        direction === 'INCREASE' ? (
            <IconManager
                icon="SvgArrowUp24"
                size={16}
                themeColors={{
                    default: {
                        'color-1': 'semantic.success',
                    },
                }}
            />
        ) : (
            <IconManager
                icon="SvgArrowDown24"
                size={16}
                themeColors={{
                    default: {
                        'color-1': 'semantic.error',
                    },
                }}
            />
        );

    return (
        <TargetItemPercentageWrapper>
            {icon}{' '}
            <TargetItemPercentageValue direction={direction} fontWeight="bold">
                {percentageValue}%
            </TargetItemPercentageValue>
        </TargetItemPercentageWrapper>
    );
};

export const TargetsCard = ({ hidden }: TargetsCardProps) => {
    const { navigate } = useNavigation();

    const { data, loading } = useQuery(TargetsCardDocument, {
        skip: hidden,
        variables: {
            input: {
                paging: {
                    limit: 2,
                    offset: 0,
                },
            },
        },
    });

    const targets =
        data?.targetList.edges.map(({ node }) => {
            const currentYear = new Date().getFullYear();
            const isOverallTarget = node.points === 'OVERALL';
            const isRecurringTarget = node.points === 'RECURRING';
            const { baseYear, targetData, targetYear } = node;

            return {
                uuid: node.uuid,
                direction: node.direction,
                metricUuid: node.metric?.uuid || '',
                name: node.name,
                percentageValue: isOverallTarget
                    ? targetData?.find((item) => item.year === targetYear)
                          ?.targetValue || 0
                    : targetData?.find((item) => item.year === currentYear + 1)
                          ?.targetValue || 0,
                unit: targetData?.find((item) => item.unit)?.unit || '',
                unitsValue: calculateTargetUnitsValue({
                    isOverallTarget,
                    targetData,
                    targetYear,
                }),
                year: isOverallTarget
                    ? Number(targetYear)
                    : isRecurringTarget
                      ? `${baseYear}-${targetYear}`
                      : targetData?.find(
                            (data) => data.year === currentYear + 1,
                        )?.year || 0,
            };
        }) || [];

    return (
        <PlaceholderCard hidden={hidden} minHeight={352}>
            <Ornament>
                <img alt="Ornament" src={TargetsBackground as string} />
            </Ornament>

            <TargetsCardWrapper>
                <TargetsHeader>
                    <PlaceholderCard.Title>Your targets</PlaceholderCard.Title>
                    <PlaceholderCard.Description>
                        Keep track of your targets.
                    </PlaceholderCard.Description>
                </TargetsHeader>

                {loading && (
                    <ContentWrapper>
                        {Array.from({ length: 2 }).map((_, index) => (
                            <Skeleton height={72} key={index} width={'100%'} />
                        ))}
                    </ContentWrapper>
                )}

                {!loading && !targets.length && (
                    <StatisticsCardEmptyState
                        action={{
                            label: 'create target',
                            onClick: () => navigate({ path: '/esg-metrics' }),
                        }}
                    >
                        There are no targets yet.
                    </StatisticsCardEmptyState>
                )}

                {!loading && !!targets.length && (
                    <ContentWrapper>
                        {targets.map((target) => (
                            <TargetItem
                                href={getPath({
                                    params: {
                                        id: target.metricUuid,
                                        targetId: target.uuid,
                                    },
                                    path: '/esg-metrics/:id/target/:targetId',
                                })}
                                key={target.uuid}
                            >
                                <TargetItemContent>
                                    <Typography.TextH6_14_20 fontWeight="bold">
                                        {target.name}
                                    </Typography.TextH6_14_20>
                                    <TargetItemValues>
                                        <TargetItemContentValues>
                                            <Typography.TextBody_12_18 fontWeight="bold">
                                                {new Intl.NumberFormat(
                                                    'en-US',
                                                    {
                                                        maximumFractionDigits: 2,
                                                        minimumFractionDigits: 0,
                                                    },
                                                )
                                                    .format(target.unitsValue)
                                                    .replace(/,/g, ' ')}{' '}
                                                {target.unit}
                                            </Typography.TextBody_12_18>

                                            {renderPercentageValue(
                                                target.percentageValue,
                                                target.direction,
                                            )}
                                        </TargetItemContentValues>

                                        <CardBadge>{target.year}</CardBadge>
                                    </TargetItemValues>
                                </TargetItemContent>
                                <IconManager
                                    icon="SvgChevronRight24"
                                    size={24}
                                    themeColors={{
                                        default: {
                                            'color-1': 'tertiary.default',
                                        },
                                    }}
                                />
                            </TargetItem>
                        ))}
                    </ContentWrapper>
                )}

                <ActionWrapper>
                    <CardLink href={getPath({ path: '/esg-metrics' })}>
                        Go to Metrics
                    </CardLink>
                </ActionWrapper>
            </TargetsCardWrapper>
        </PlaceholderCard>
    );
};
